import React from "react";
import {
	BankAccountEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { formatCurrency } from "../../lib/utils/utils-functions";
import { PaymentReminderDocument } from "../../pages/debitoren/mahnwesen/payment-reminder.types";
import {
	OPEN_EPSILON,
	buildReminderPaymentSlips,
} from "../../pages/debitoren/mahnwesen/utils/payment-reminder-document";
import {
	formatReminderDate,
	getOpenReminderFees,
	resolveReminderPlaceholders,
} from "../../pages/debitoren/mahnwesen/utils/reminder-placeholders";
import "../fonts.css";
import { PdfFooter } from "../pdf-template-components/pdf-footer.component";
import { PdfLayout } from "../pdf-template-components/pdf-layout.component";
import { PdfLetterhead } from "../pdf-template-components/pdf-letterhead.component";
import { PdfRecipientAddress } from "../pdf-template-components/pdf-recipient-address.component";
import { PdfTitleAndDate } from "../pdf-template-components/pdf-title-and-date.component";
import { QrPaymentSlip } from "../pdf-template-components/qr-payment-slip/qr-payment-slip.component";

export const PAYMENT_REMINDER_PAGE_BREAK = "pdf-page-break";

export interface PaymentReminderTemplateProps {
	document: PaymentReminderDocument;
	organization: OrganizationEntityType | null;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	bankAccount: BankAccountEntityType | null;
	currencyConverterValues?: Record<string, string>;
}

const FONT_SIZE = { sm: "8pt", md: "9pt", lg: "10pt" };

export const PaymentReminderTemplate: React.FC<
	PaymentReminderTemplateProps
> = ({
	document,
	organization,
	pdfTemplateConfiguration,
	bankAccount,
	currencyConverterValues,
}) => {
	if (!organization || !pdfTemplateConfiguration) {
		return null;
	}

	const templateType = document.reminder.template_type;
	const config = pdfTemplateConfiguration[templateType];
	const general = pdfTemplateConfiguration.general;
	const { invoice, fees } = document;
	const currency = invoice.currency;
	const openFees = fees.filter((fee) => fee.open_amount > OPEN_EPSILON);
	const feeVatRates = [
		...new Set(
			openFees.map((fee) => fee.vat_rate).filter((rate) => rate > 0)
		),
	];
	const total = invoice.open_amount + getOpenReminderFees(document);
	const paragraphs = resolveReminderPlaceholders(config.bodyText, document)
		.split(/\n\s*\n/)
		.filter((paragraph) => paragraph.trim().length > 0);
	const slips = bankAccount
		? buildReminderPaymentSlips(document, bankAccount)
		: [];
	const invoiceSlip = slips.find(
		(slip) => slip.invoiceNumber === invoice.doc_number
	);
	const feeSlips = slips.filter((slip) => slip !== invoiceSlip);

	const cellStyle: React.CSSProperties = { padding: "0.8mm 1.5mm" };
	const amountCellStyle: React.CSSProperties = {
		...cellStyle,
		textAlign: "right",
	};

	return (
		<>
			<PdfLayout pdfTemplateConfiguration={pdfTemplateConfiguration}>
				<PdfLetterhead
					organization={organization}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					pdfType={templateType}
					mwstNumber={organization.mwst_number ?? ""}
				/>
				<PdfRecipientAddress
					recipient={document.recipient}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					template={templateType}
				/>
				<div
					aria-label="Dokumenteninhalt"
					style={{
						paddingTop: "5mm",
						paddingLeft: "5mm",
						paddingRight: "10mm",
					}}
				>
					<PdfTitleAndDate
						generalConfig={general}
						title={config.title}
						date={document.reminder.sent_at}
					/>
					<div
						aria-label="Brieftext"
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "3mm",
							paddingTop: "6mm",
							fontSize: "10pt",
							whiteSpace: "pre-wrap",
						}}
					>
						{paragraphs.map((paragraph, index) => (
							<p key={index}>{paragraph}</p>
						))}
					</div>
					<table
						aria-label="Offene Posten"
						style={{
							width: "100%",
							marginTop: "3mm",
							borderCollapse: "collapse",
							fontSize: FONT_SIZE[general.size ?? "lg"],
						}}
					>
						<thead>
							<tr
								style={{
									backgroundColor:
										general.tableBackgroundColor ??
										"#d3d3d3",
									borderBottom: "1px solid black",
								}}
							>
								<th style={{ ...cellStyle, textAlign: "left" }}>
									Nr.
								</th>
								<th style={{ ...cellStyle, textAlign: "left" }}>
									Art
								</th>
								<th style={{ ...cellStyle, textAlign: "left" }}>
									Datum
								</th>
								<th style={amountCellStyle}>Betrag</th>
								<th style={amountCellStyle}>Bezahlt</th>
								<th style={amountCellStyle}>Offen</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td style={cellStyle}>{invoice.doc_number}</td>
								<td style={cellStyle}>Rechnung</td>
								<td style={cellStyle}>
									{formatReminderDate(invoice.date)}
								</td>
								<td style={amountCellStyle}>
									{formatCurrency(invoice.value, currency)}
								</td>
								<td style={amountCellStyle}>
									{formatCurrency(
										invoice.paid_amount,
										currency
									)}
								</td>
								<td style={amountCellStyle}>
									{formatCurrency(
										invoice.open_amount,
										currency
									)}
								</td>
							</tr>
							{openFees.map((fee) => (
								<tr key={fee.invoice_id}>
									<td style={cellStyle}>{fee.doc_number}</td>
									<td style={cellStyle}>Mahngebühr</td>
									<td style={cellStyle}>
										{formatReminderDate(fee.date)}
									</td>
									<td style={amountCellStyle}>
										{formatCurrency(fee.value, currency)}
									</td>
									<td style={amountCellStyle}>
										{formatCurrency(
											fee.value - fee.open_amount,
											currency
										)}
									</td>
									<td style={amountCellStyle}>
										{formatCurrency(
											fee.open_amount,
											currency
										)}
									</td>
								</tr>
							))}
							<tr
								style={{
									borderTop: "1px solid black",
									fontWeight: general.isTotalBold ? 700 : 600,
								}}
							>
								<td style={cellStyle} colSpan={5}>
									Total offen
									{feeVatRates.length > 0 && (
										<span
											style={{
												fontSize: "0.85em",
												fontWeight: 400,
											}}
										>
											{" "}
											(Mahngebühren inkl.{" "}
											{feeVatRates.join(" / ")} % MwSt.)
										</span>
									)}
								</td>
								<td style={amountCellStyle}>
									{formatCurrency(total, currency)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<PdfFooter
					templateType={templateType}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					currencyConverterValues={currencyConverterValues}
				/>
				{invoiceSlip && (
					<QrPaymentSlip
						pdfTemplateConfiguration={pdfTemplateConfiguration}
						paymentSlipData={invoiceSlip}
						textAbove={`Rechnung ${invoice.doc_number}`}
					/>
				)}
			</PdfLayout>
			{feeSlips.map((slip) => (
				<div
					key={slip.invoiceNumber}
					className={PAYMENT_REMINDER_PAGE_BREAK}
				>
					<PdfLayout
						pdfTemplateConfiguration={pdfTemplateConfiguration}
					>
						<PdfTitleAndDate
							generalConfig={general}
							title={`Mahngebühr ${slip.invoiceNumber} zu Rechnung ${invoice.doc_number}`}
							date={document.reminder.sent_at}
						/>
						<QrPaymentSlip
							pdfTemplateConfiguration={pdfTemplateConfiguration}
							paymentSlipData={slip}
							textAbove={`Mahngebühr ${slip.invoiceNumber}`}
						/>
					</PdfLayout>
				</div>
			))}
		</>
	);
};
